import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
    GetDatasourceDocumentsParams,
    getDatasourceDocumentsRevalidated
} from '@/services/data_source';
import { useAgentStore } from '@/stores/agentStore';
import { useChatSessionsStore } from '@/stores/chatSessions';
import { DataSource, Item, Skills } from '@/types';

export const useDataSourcesStore = defineStore('dataSources', () => {
    const agentStore = useAgentStore();
    const chatSessionsStore = useChatSessionsStore();

    const selectedDataSourcesAll = ref<DataSource[]>([]);
    const selectedAttachedFiles = ref<Item[]>([]);

    const dataSourcesAll = ref<DataSource[]>([]);
    const attachedFilesDataSource = ref<DataSource | null>(null);

    const paginatedAttachedFiles = ref<Item[]>([]);
    const attachedFilesCount = ref<number>(0);

    const reloadDataSources = ref<boolean>(false);
    const reloadDataSourceDocuments = ref<boolean>(false);
    const reloadToolbarSources = ref<boolean>(false);

    const attachedFilesLoading = ref<boolean>(false);
    const documentsLoading = ref<boolean>(false);
    const dataSourcesLoading = ref<boolean>(false);

    const publicDatasources = computed(() => {
        return dataSourcesAll.value.filter(
            (dataSource) => dataSource.visibility === 'public'
        );
    });
    const privateDatasources = computed(() => {
        return dataSourcesAll.value.filter(
            (dataSource) => dataSource.visibility === 'private'
        );
    });
    const sharedDatasources = computed(() => {
        return dataSourcesAll.value.filter(
            (dataSource) => dataSource.visibility === 'shared'
        );
    });
    const selectedDataSourceIds = computed(() => {
        return selectedDataSourcesAll.value.map((dataSource) => dataSource.id);
    });
    const selectedDatasources = computed(() => {
        return selectedDataSourcesAll.value.filter(
            (dataSource) => !dataSource.chat_session_id
        );
    });
    const selectedAttachedFilesDataSources = computed(() => {
        return selectedDataSourcesAll.value.filter(
            (dataSource) => dataSource.chat_session_id
        );
    });
    const selectedAttachedFilesIds = computed(() => {
        return selectedAttachedFiles.value
            .map((item) => item.datasource_item_id)
            .filter((id): id is string => id !== undefined);
    });
    const isAttachedFilesDataSourceSelected = computed(() => {
        return selectedAttachedFilesDataSources.value.length > 0;
    });
    const shouldUseChatWithDocuments = computed(() => {
        return (
            selectedAttachedFiles.value.length > 0 ||
            selectedDatasources.value.length > 0
        );
    });

    const hasMoreItems = computed(() => {
        if (!attachedFilesCount.value) return false;

        // Active items are always shown first, so we need to check if there are more non-active items
        const activeItemsCount = selectedAttachedFiles.value.length;
        const nonActiveItemsLoaded =
            paginatedAttachedFiles.value.length - activeItemsCount;
        const totalNonActiveItems = attachedFilesCount.value - activeItemsCount;

        return (
            nonActiveItemsLoaded < totalNonActiveItems &&
            !attachedFilesLoading.value
        );
    });

    const isDatasourceNeeded = computed(() => {
        return (
            agentStore.selectedSkill === Skills.NONE ||
            (agentStore.selectedSkill === Skills.CHAT_WITH_DOCUMENTS &&
                selectedDatasources.value.length === 0 &&
                selectedAttachedFiles.value.length === 0) ||
            chatSessionsStore.isUploading
        );
    });

    // To handle caching of data source items
    const etagCache = new Map<string, string>();
    const itemsCache = new Map<
        string,
        { items: Item[]; count: number; nextOffset: number | null }
    >();

    async function loadDataSourceItems(
        key: string,
        params: GetDatasourceDocumentsParams
    ) {
        const res = await getDatasourceDocumentsRevalidated(params, {
            etag: etagCache.get(key)
        });

        if (res.notModified) {
            return itemsCache.get(key)!;
        }

        if (res.etag) {
            etagCache.set(key, res.etag);
        }

        const payload = {
            items: res.items!,
            count: res.count!,
            nextOffset: res.nextOffset
        };
        itemsCache.set(key, payload);

        return payload;
    }

    const setSelectedDataSources = (dataSources: DataSource[]) => {
        selectedDataSourcesAll.value = dataSources;
    };
    const setSelectedAttachedFiles = (items: Item[]) => {
        selectedAttachedFiles.value = items;
    };
    const setPaginatedAttachedFiles = (items: Item[]) => {
        paginatedAttachedFiles.value = items;
    };
    const setAttachedFilesCount = (count: number) => {
        attachedFilesCount.value = count;
    };
    const setAttachedFilesLoading = (loading: boolean) => {
        attachedFilesLoading.value = loading;
    };
    const setDocumentsLoading = (loading: boolean) => {
        documentsLoading.value = loading;
    };
    const setDataSourcesLoading = (loading: boolean) => {
        dataSourcesLoading.value = loading;
    };
    const setDataSources = (dataSources: DataSource[]) => {
        dataSourcesAll.value = dataSources;
    };
    const setAttachedFilesDataSource = (dataSource: DataSource) => {
        attachedFilesDataSource.value = dataSource;
    };

    const resetStore = () => {
        selectedDataSourcesAll.value = [];
        selectedAttachedFiles.value = [];
        paginatedAttachedFiles.value = [];
        attachedFilesCount.value = 0;
        attachedFilesLoading.value = false;
    };

    /**
     * Add an item to paginatedAttachedFiles if it's not already there
     * This ensures items selected from search are visible when search query is cleared
     */
    const addToPaginatedAttachedFilesIfNotExists = (item: Item) => {
        const exists = isItemInPaginatedAttachedFiles(item);
        if (!exists) {
            paginatedAttachedFiles.value = [
                item,
                ...paginatedAttachedFiles.value
            ];
        }
    };

    const isItemInPaginatedAttachedFiles = (item: Item): boolean => {
        return paginatedAttachedFiles.value.some(
            (file) => file.datasource_item_id === item.datasource_item_id
        );
    };

    return {
        // ALL STATES
        selectedDataSourcesAll,
        selectedDataSourceIds,
        selectedDatasources,
        selectedAttachedFilesDataSources,
        selectedAttachedFiles,
        isAttachedFilesDataSourceSelected,
        reloadDataSources,
        reloadDataSourceDocuments,
        reloadToolbarSources,
        paginatedAttachedFiles,
        attachedFilesCount,
        selectedAttachedFilesIds,
        hasMoreItems,
        attachedFilesLoading,
        isDatasourceNeeded,
        documentsLoading,
        dataSourcesLoading,
        publicDatasources,
        privateDatasources,
        sharedDatasources,
        attachedFilesDataSource,
        dataSourcesAll,
        shouldUseChatWithDocuments,

        // ALL ACTIONS
        setSelectedDataSources,
        setSelectedAttachedFiles,
        setPaginatedAttachedFiles,
        setAttachedFilesCount,
        setAttachedFilesLoading,
        setDocumentsLoading,
        setDataSourcesLoading,
        setDataSources,
        setAttachedFilesDataSource,
        resetStore,
        loadDataSourceItems,
        addToPaginatedAttachedFilesIfNotExists,
        isItemInPaginatedAttachedFiles
    };
});
